import React, { useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { useAppDispatch, useAppSelector } from "../hooks/useRedux";
import { submitContactForm, clearContactData } from "../features/contact/contact.slice";
import "../scss/contact-us.scss";

interface ContactFormData {
  fullName: string;
  email: string;
  phone: string;
  message: string;
}

interface ContactUsProps {
  title?: string;
  subtitle?: string;
  buttonText?: string;
}

export const ContactUs: React.FC<ContactUsProps> = ({ title = "CONTACT_US_TITLE", subtitle = "CONTACT_US_SUBTITLE", buttonText = "SEND_MESSAGE" }) => {
  const { t } = useTranslation();
  const [showSuccess, setShowSuccess] = useState(false);
  const dispatch = useAppDispatch();
  const { contactData, loading, error } = useAppSelector((state) => state?.contact);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ContactFormData>();

  const onSubmit = useCallback(async (data: ContactFormData) => {
    try {
      const contactPayload = {
        name: data.fullName,
        email: data.email,
        phoneNumber: data.phone,
        message: data.message,
        subject: "Contact Form Submission",
      };

      const result = await dispatch(submitContactForm(contactPayload));
      
      if (submitContactForm.fulfilled.match(result)) {
        setShowSuccess(true);
        reset();
        toast.success(t("MESSAGE_SENT_SUCCESSFULLY") as string);

        // Hide success message after 5 seconds
        setTimeout(() => setShowSuccess(false), 5000);
      } else {
        toast.error(t("MESSAGE_SEND_ERROR") as string);
      }
    } catch (error) {
      toast.error(t("MESSAGE_SEND_ERROR") as string);
    }
  }, [dispatch, reset, t]);

  return (
    <div className="contact-us-page-wrapper">
      {/* Main Content Section */}
      <section className="contact-main-section">
        <div className="contact-container">
          <div className="contact-section-title">
            <h2>{t("CONTACT_US_SECTION_TITLE")}</h2>
          </div>
          <div className="contact-content-wrapper">
            {/* Contact Form */}
            <div className="contact-form-section">
              <div className="contact-form-container">
                <div className="contact-form-header">
                  <h3>{t("CONTACT_US_FORM_TITLE")}</h3>
                  <p>{t("CONTACT_US_FORM_DESCRIPTION")}</p>
                </div>

                <form onSubmit={handleSubmit(onSubmit)} className="contact-form">
                  {/* Full Name Field */}
                  <div className="form-group">
                    <label className="form-label">{t("CONTACT_US_FORM_FULL_NAME_LABEL")}</label>
                    <input
                      type="text"
                      className={`form-input ${errors.fullName ? "error" : ""}`}
                      placeholder={t("CONTACT_US_FORM_FULL_NAME_PLACEHOLDER")}
                      {...register("fullName", {
                        required: t("CONTACT_US_FORM_FULL_NAME_REQUIRED"),
                        minLength: { value: 2, message: t("CONTACT_US_FORM_FULL_NAME_MIN_LENGTH") },
                      })}
                    />
                    {errors.fullName && <span className="error-message">{errors.fullName.message}</span>}
                  </div>

                  {/* Email Field */}
                  <div className="form-group">
                    <label className="form-label">{t("CONTACT_US_FORM_EMAIL_LABEL")}</label>
                    <input
                      type="email"
                      className={`form-input ${errors.email ? "error" : ""}`}
                      placeholder={t("CONTACT_US_FORM_EMAIL_PLACEHOLDER")}
                      {...register("email", {
                        required: t("CONTACT_US_FORM_EMAIL_REQUIRED"),
                        pattern: {
                          value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                          message: t("CONTACT_US_FORM_EMAIL_INVALID"),
                        },
                      })}
                    />
                    {errors.email && <span className="error-message">{errors.email.message}</span>}
                  </div>

                  {/* Phone Field */}
                  <div className="form-group">
                    <label className="form-label">{t("CONTACT_US_FORM_PHONE_LABEL")}</label>
                    <input
                      type="tel"
                      className={`form-input ${errors.phone ? "error" : ""}`}
                      placeholder={t("CONTACT_US_FORM_PHONE_PLACEHOLDER")}
                      {...register("phone", {
                        pattern: {
                          value: /^[\+]?[1-9][\d]{0,15}$/,
                          message: t("CONTACT_US_FORM_PHONE_INVALID"),
                        },
                      })}
                    />
                    {errors.phone && <span className="error-message">{errors.phone.message}</span>}
                  </div>

                  {/* Message Field */}
                  <div className="form-group">
                    <label className="form-label">{t("CONTACT_US_FORM_MESSAGE_LABEL")}</label>
                    <textarea
                      className={`form-input form-textarea ${errors.message ? "error" : ""}`}
                      placeholder={t("CONTACT_US_FORM_MESSAGE_PLACEHOLDER")}
                      rows={6}
                      {...register("message", {
                        required: t("CONTACT_US_FORM_MESSAGE_REQUIRED"),
                        minLength: { value: 10, message: t("CONTACT_US_FORM_MESSAGE_MIN_LENGTH") },
                      })}
                    />
                    {errors.message && <span className="error-message">{errors.message.message}</span>}
                  </div>

                  {/* Submit Button */}
                  <button type="submit" className="contact-submit-btn" disabled={loading}>
                    {loading ? t("CONTACT_US_FORM_SENDING") : t("CONTACT_US_FORM_SUBMIT")}
                  </button>
                </form>
                {/* Privacy Note */}
                <div className="privacy-note">
                  <p>
                    <strong>{t("CONTACT_US_PRIVACY_NOTE_TITLE")}</strong> {t("CONTACT_US_PRIVACY_NOTE_TEXT")}
                  </p>
                </div>
              </div>
            </div>

            {/* Contact Info Sidebar */}
            <div className="contact-info-section">
              <div className="contact-info-header">
                <h3>{t("CONTACT_US_SIDEBAR_TITLE")}</h3>
                <p>{t("CONTACT_US_SIDEBAR_DESCRIPTION")}</p>
              </div>

              <div className="contact-info-items">
                {/* Contact Details */}
                <div className="contact-info-item">
                  <div className="contact-info-icon">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </div>
                  <div className="contact-info-content">
                    <h4>{t("CONTACT_US_SIDEBAR_PHONE_TITLE")}</h4>
                    <p>010-264 88 10</p>
                  </div>
                </div>

                {/* Email */}
                <div className="contact-info-item">
                  <div className="contact-info-icon">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      <polyline points="22,6 12,13 2,6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </div>
                  <div className="contact-info-content">
                    <h4>{t("CONTACT_US_SIDEBAR_EMAIL_TITLE")}</h4>
                    <p>support@invozio.com</p>
                  </div>
                </div>

                {/* Opening Hours */}
                <div className="contact-info-item">
                  <div className="contact-info-icon">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
                      <polyline points="12,6 12,12 16,14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </div>
                  <div className="contact-info-content">
                    <h4>{t("CONTACT_US_SIDEBAR_HOURS_TITLE")}</h4>
                    <p>{t("CONTACT_US_SIDEBAR_HOURS_TEXT")}</p>
                    <p>{t("CONTACT_US_SIDEBAR_HOURS_TEXT_2")}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactUs;
